import { useAppUser } from '../admin/AppUserContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, RefreshCw } from 'lucide-react';
import Header from '../components/Header';
import VisualReportEditor from '../components/VisualReportEditor';
import { companiesApi, type Company } from '../services/api';
import {
  adaptReportConfiguration,
  isReportDocumentV2,
  type ReportDocumentV2,
} from '../reports/model';
import {
  loadReportConfiguration,
  loadReportSource,
  saveReportConfiguration,
  type ReportConfiguration,
} from '../reports/api';
import './CompanyWorkPages.css';

export default function VisualReportPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const [params] = useSearchParams();
  const reportId = params.get('reportId') || undefined;
  return companyId ? (
    <CompanyReport
      key={`${companyId}:${reportId || 'default'}`}
      companyId={companyId}
      reportId={reportId}
    />
  ) : (
    <p role="alert">Choose a company to create a report.</p>
  );
}

function CompanyReport({ companyId, reportId }: { companyId: string; reportId?: string }) {
  const { user } = useAppUser();
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState<{
    company: Company;
    document: ReportDocumentV2;
    saved: boolean;
  } | null>(null);
  const [error, setError] = useState('');
  const [generation, setGeneration] = useState(0);
  const [cacheReportId, setCacheReportId] = useState<string | null>(null);
  const identity = useRef<{ id: string; updatedAt?: string; exists: boolean }>({
    id: crypto.randomUUID(),
    exists: false,
  });

  useEffect(() => {
    let cancelled = false;
    setLoaded(null);
    setError('');
    const load = async () => {
      try {
        const companyResult = await companiesApi.get(companyId);
        if (!companyResult.success || !companyResult.data)
          throw new Error(companyResult.error || 'This company could not be loaded.');
        const company = companyResult.data;
        const [configuration, source] = await Promise.all([
          reportId
            ? loadReportConfiguration(companyId, reportId)
            : loadReportConfiguration(companyId),
          loadReportSource(company),
        ]);
        if (cancelled) return;
        if (configuration?.document && !isReportDocumentV2(configuration.document)) {
          throw new Error(
            'The saved report needs recovery before editing. Its stored contents have been preserved.'
          );
        }
        identity.current = configuration
          ? { id: configuration.id, updatedAt: configuration.updatedAt, exists: true }
          : { id: crypto.randomUUID(), exists: false };
        setCacheReportId(configuration?.id || null);
        setLoaded({
          company,
          document: adaptReportConfiguration(configuration, company, source, user?.fullName || ''),
          saved: !!configuration?.document,
        });
      } catch (failure) {
        if (!cancelled)
          setError(failure instanceof Error ? failure.message : 'Your report could not be loaded.');
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [companyId, reportId, generation, user?.id]);

  const save = useCallback(
    async (document: ReportDocumentV2) => {
      const configuration: ReportConfiguration = await saveReportConfiguration(
        companyId,
        document,
        identity.current
      );
      identity.current = { id: configuration.id, updatedAt: configuration.updatedAt, exists: true };
      setCacheReportId(configuration.id);
    },
    [companyId]
  );

  return (
    <div className="company-work-page company-report-page company-workspace-screen flex flex-col overflow-hidden">
      <Header
        title="Strategy reports"
        subtitle="A clear story. A confident presentation."
        fullWidth
        company={loaded ? { name: loaded.company.name, logo: loaded.company.logo } : undefined}
        backToStrategy={{
          url: `/company/${companyId}/strategy/formulation/summary`,
          label: 'Back to Summary',
        }}
      />
      {error ? (
        <div className="report-load-state" role="alert">
          <h2>We couldn’t open your report</h2>
          <p>{error}</p>
          <button type="button" onClick={() => setGeneration((value) => value + 1)}>
            <RefreshCw size={16} /> Try again
          </button>
        </div>
      ) : !loaded ? (
        <div className="report-load-state" role="status">
          <Loader2 className="animate-spin" />
          <p>Gathering your strategy and saved report…</p>
        </div>
      ) : (
        <div className="company-report-editor flex-1 overflow-hidden min-h-0 min-w-0">
          <VisualReportEditor
            key={`${companyId}-${identity.current.id}-${generation}`}
            initialDocument={loaded.document}
            initiallySaved={loaded.saved}
            draftKey={`stratplan:report-draft:${user?.id || 'session'}:${companyId}:${cacheReportId || 'new'}`}
            draftIdentityKey={identity.current.id}
            legacyDraftKey={
              reportId ? undefined : `stratplan:report-draft:${user?.id || 'session'}:${companyId}`
            }
            readOnly={!(loaded.company.capabilities?.editPlan ?? loaded.company.role !== 'viewer')}
            onSave={save}
            onRefresh={() => loadReportSource(loaded.company)}
            onReload={() => setGeneration((value) => value + 1)}
            onClose={() => navigate(`/company/${companyId}/strategy/formulation/summary`)}
          />
        </div>
      )}
    </div>
  );
}
